import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createWorkflow } from '../apis/workflow/post/createWorkflow';
import { useWorkflowContext } from '../contexts/WorkflowContext';
import { UseAuth } from '../contexts/AuthContext';
import { PaymentWorkflowType } from '../components/container/forms/types/api/graph.types';
import { workflowTypeToFolder } from '../utils/workflowTypeToFolder';
import { folderToPath } from '.././utils/workflowsHelper';
import { DUMMY_OVERTIME_WORKFLOW_ID } from '../components/container/forms/schemas/OvertimeApproval/constants';
import { isOvertimeApprovalBypassEnabled } from '../utils/overtimeApprovalBypass';
import {
  getTestingDummyWorkflowId,
  shouldBypassWorkflowBackend,
} from '../utils/workflowTestingBypass';

export const useWorkflowCreation = () => {
  const { getAccessToken, spoofedUser } = UseAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { triggerRefresh } = useWorkflowContext();

  const resetModalState = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const createRequest = async (
    clientUno: string,
    matterUno: string,
    workflowType: PaymentWorkflowType
  ) => {
    const folderName = workflowTypeToFolder[workflowType];
    if (!folderName) {
      console.error('Unknown workflow type for navigation', { workflowType });
      return false;
    }
    const workflowSlug = folderToPath(folderName).replace('/workflows/', '');

    // Testing: registry `permissions: true` — no createWorkflow API.
    if (shouldBypassWorkflowBackend(workflowType)) {
      resetModalState();
      navigate(
        `/request/${workflowSlug}/${getTestingDummyWorkflowId(workflowSlug)}`
      );
      return true;
    }

    // TEMPORARY: Overtime Approval backend permissions are not ready yet.
    if (
      isOvertimeApprovalBypassEnabled &&
      workflowType === PaymentWorkflowType.OvertimeApproval
    ) {
      resetModalState();
      navigate(`/request/overtime-approval/${DUMMY_OVERTIME_WORKFLOW_ID}`);
      return true;
    }

    try {
      const response = await createWorkflow({
        type: workflowType,
        clientUno,
        matterUno,
        getAccessToken,
        spoofedUser: spoofedUser?.value || null,
      });

      if (response.success) {
        triggerRefresh();
        resetModalState();
        navigate(`/request/${workflowSlug}/${response.data.id}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to create workflow:', error);
      return false;
    }
  };

  return {
    isModalOpen,
    setIsModalOpen,
    createRequest,
    resetModalState,
  };
};
